/**
 * Where a cable runs, and how it is drawn where it crosses another.
 *
 * The route is React Flow's smooth-step route for a right-hand output into a
 * left-hand input, ported here so the canvas has one implementation of it:
 * the same corners, the same rounded bends, the same label point. With no
 * crossings `cablePath` returns exactly the string `getSmoothStepPath` would,
 * so switching crossings off draws what the canvas always drew.
 *
 * Crossings follow one rule, the one every reference drawing uses: the
 * horizontal line stays whole and the vertical line gives way. Cables leave
 * and enter devices horizontally and climb in a vertical middle run, so the
 * middle run yields and the ends near the devices stay clean. The one
 * exception is a selected cable, which never breaks: whatever it crosses gives
 * way to it instead, so the cable you grabbed reads as one unbroken line.
 *
 * Pure, so the routes and crossings are testable without a browser.
 */

export type Point = { x: number; y: number }

/**
 * Where a cable gives way: a crossing on one of its vertical runs, or, when it
 * is the horizontal cable that has to yield to a selected one, on a horizontal
 * run.
 */
export type Jump = Point & { on?: 'horizontal' }

/**
 * The gap a smooth-step route leaves before its first bend, in flow units.
 * Matches the `offset` default in `getSmoothStepPath`.
 */
export const STEP_GAP = 20

/** Corner radius of a cable's bends, in flow units. */
export const CABLE_RADIUS = 8

/** Half the length of a hop or a gap where a cable gives way. */
export const JUMP_SIZE = 5

/** Half the width of the bars that cap a gap. */
const CAP_HALF = 4

/** Jumps closer than this along one run are drawn as one. */
const JUMP_MERGE = 2

/**
 * How far a jump may sit from the run the canvas actually drew. The crossings
 * are found from the node positions a frame before React Flow draws them, so
 * mid-drag the two can differ by a pixel or two; lanes are 16 apart, so a
 * jump can never land on the wrong cable.
 */
const JUMP_MATCH = 3

const EPS = 1e-6

export const CROSSING_STYLES = ['off', 'hop', 'caps'] as const
export type CrossingStyle = (typeof CROSSING_STYLES)[number]

export const CROSSING_LABELS: Record<CrossingStyle, string> = {
  off: 'Off',
  hop: 'Hop',
  caps: 'Cap',
}

export function isCrossingStyle(value: unknown): value is CrossingStyle {
  return typeof value === 'string' && (CROSSING_STYLES as readonly string[]).includes(value)
}

/**
 * Which way to push a lane aside.
 *
 * Every cable leaves a right-hand output and enters a left-hand input. When
 * the input is far enough to the right, the route is out, down (or up) a
 * vertical trunk, and in — so the trunk is separated sideways ('x'). When it
 * is not, the route doubles back through a horizontal trunk, which is
 * separated up and down ('y'). What matters is how the route bends, not
 * which distance is larger: a cable dropping 400 units to a device 200 to the
 * right still has a vertical trunk.
 */
export function laneAxis(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): 'x' | 'y' {
  void sourceY
  void targetY
  return targetX - sourceX > 2 * STEP_GAP ? 'x' : 'y'
}

export type CableRoute = {
  /** Every point the route passes, ends included, as React Flow lists them. */
  points: Point[]
  /** Where the cable's label sits. */
  label: Point
}

/**
 * The route from an output at (sourceX, sourceY) to an input at (targetX,
 * targetY), its trunk shifted `laneOffset` into its lane (see `assignLanes`).
 *
 * A port of `getPoints` in @xyflow/system for the one case the canvas uses,
 * Position.Right into Position.Left, kept arithmetic for arithmetic so the
 * floats come out identical.
 */
export function cableRoute(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  laneOffset = 0,
): CableRoute {
  const axis = laneAxis(sourceX, sourceY, targetX, targetY)
  const source = { x: sourceX, y: sourceY }
  const target = { x: targetX, y: targetY }
  const sourceGapped = { x: sourceX + STEP_GAP, y: sourceY }
  const targetGapped = { x: targetX - STEP_GAP, y: targetY }

  // The lane offset moves the trunk along the axis it is separated on; the
  // other centre is React Flow's default.
  const centerX = axis === 'x' ? (sourceX + targetX) / 2 + laneOffset : sourceGapped.x + (targetGapped.x - sourceGapped.x) * 0.5
  const centerY = axis === 'y' ? (sourceY + targetY) / 2 + laneOffset : (sourceGapped.y + targetGapped.y) / 2

  // Out, along a vertical trunk, and in; or, when the input is not far enough
  // to the right, out, along a horizontal trunk that doubles back, and in.
  const forward = sourceGapped.x < targetGapped.x
  const [first, last]: [Point, Point] = forward
    ? [
        { x: centerX, y: sourceGapped.y },
        { x: centerX, y: targetGapped.y },
      ]
    : [
        { x: sourceGapped.x, y: centerY },
        { x: targetGapped.x, y: centerY },
      ]

  const points = [
    source,
    ...(sourceGapped.x !== first.x || sourceGapped.y !== first.y ? [sourceGapped] : []),
    first,
    last,
    ...(targetGapped.x !== last.x || targetGapped.y !== last.y ? [targetGapped] : []),
    target,
  ]
  return { points, label: { x: centerX, y: centerY } }
}

const distance = (a: Point, b: Point) => Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2))

/**
 * A bend as `getBend` draws it: the line to where the curve starts, then the
 * curve. `end` is where the curve leaves off and the next straight run begins.
 */
type Bend = { start: Point; end: Point; command: string }

function bendAt(a: Point, b: Point, c: Point, size: number): Bend {
  const bendSize = Math.min(distance(a, b) / 2, distance(b, c) / 2, size)
  const { x, y } = b
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return { start: b, end: b, command: `L${x} ${y}` }
  }
  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1
    const yDir = a.y < c.y ? 1 : -1
    const start = { x: x + bendSize * xDir, y }
    const end = { x, y: y + bendSize * yDir }
    return { start, end, command: `L ${start.x},${y}Q ${x},${y} ${x},${end.y}` }
  }
  const xDir = a.x < c.x ? 1 : -1
  const yDir = a.y < c.y ? -1 : 1
  const start = { x, y: y + bendSize * yDir }
  const end = { x: x + bendSize * xDir, y }
  return { start, end, command: `L ${x},${start.y}Q ${x},${y} ${end.x},${y}` }
}

const NO_RUN = { commands: '', caps: '' }

/**
 * The jumps on one straight run from `from` to `to`, as path commands that
 * stop short of `to`, plus any cap bars.
 *
 * Worked in run coordinates: `u` along the run, `w` across it, so a vertical
 * run and a horizontal one share the arithmetic. A hop always bulges the same
 * way whichever way the run travels: right off a vertical run, up off a
 * horizontal one.
 */
function jumpsAlong(from: Point, to: Point, jumps: readonly Jump[], style: CrossingStyle) {
  if (style === 'off' || jumps.length === 0) return NO_RUN
  const vertical = Math.abs(from.x - to.x) < EPS && Math.abs(from.y - to.y) >= EPS
  const horizontal = Math.abs(from.y - to.y) < EPS && Math.abs(from.x - to.x) >= EPS
  if (!vertical && !horizontal) return NO_RUN

  const u0 = vertical ? from.y : from.x
  const u1 = vertical ? to.y : to.x
  const w = vertical ? to.x : to.y
  const dir = u1 > u0 ? 1 : -1
  const along = (u: number) => (u - u0) * dir
  const length = along(u1)

  // Jumps that sit on this run with room for the whole jump before either end.
  const here = jumps
    .filter((jump) => (jump.on === 'horizontal') === horizontal)
    .filter((jump) => Math.abs((vertical ? jump.x : jump.y) - w) <= JUMP_MATCH)
    .map((jump) => along(vertical ? jump.y : jump.x))
    .filter((at) => at - JUMP_SIZE > EPS && length - at - JUMP_SIZE > EPS)
    .sort((a, b) => a - b)
  if (here.length === 0) return NO_RUN

  // Neighbours too close for a straight piece between them become one jump.
  const spans: Array<[number, number]> = []
  for (const at of here) {
    const open = spans[spans.length - 1]
    if (open && at - JUMP_SIZE - open[1] < JUMP_MERGE) open[1] = at + JUMP_SIZE
    else spans.push([at - JUMP_SIZE, at + JUMP_SIZE])
  }

  const point = (distanceAlong: number) => {
    const u = u0 + distanceAlong * dir
    return vertical ? `${w},${u}` : `${u},${w}`
  }
  const sweep = dir > 0 ? 1 : 0
  let commands = ''
  let caps = ''
  for (const [lo, hi] of spans) {
    const enter = point(lo)
    const leave = point(hi)
    if (style === 'hop') {
      const radii = vertical ? `${JUMP_SIZE} ${(hi - lo) / 2}` : `${(hi - lo) / 2} ${JUMP_SIZE}`
      commands += `L ${enter}A ${radii} 0 0 ${sweep} ${leave}`
    } else {
      commands += `L ${enter}M ${leave}`
      for (const end of [lo, hi]) {
        const u = u0 + end * dir
        caps += vertical ? `M ${w - CAP_HALF},${u}H ${w + CAP_HALF}` : `M ${u},${w - CAP_HALF}V ${w + CAP_HALF}`
      }
    }
  }
  return { commands, caps }
}

/**
 * The SVG path for a route, with a jump at each of `jumps` that sits on one of
 * its straight runs. `caps` holds the bars for a capped gap, drawn solid on
 * their own so a dashed cable's pattern cannot eat them.
 */
export function cablePath(
  points: readonly Point[],
  jumps: readonly Jump[] = [],
  style: CrossingStyle = 'off',
  radius = CABLE_RADIUS,
): { path: string; caps: string } {
  const start = points[0]
  const end = points[points.length - 1]
  if (!start || !end) return { path: '', caps: '' }
  let path = `M${start.x} ${start.y}`
  let caps = ''
  let pen = start
  for (let i = 1; i < points.length - 1; i++) {
    const [a, b, c] = [points[i - 1], points[i], points[i + 1]]
    if (!a || !b || !c) continue
    const bend = bendAt(a, b, c, radius)
    const run = jumpsAlong(pen, bend.start, jumps, style)
    path += run.commands + bend.command
    caps += run.caps
    pen = bend.end
  }
  const run = jumpsAlong(pen, end, jumps, style)
  path += run.commands + `L${end.x} ${end.y}`
  caps += run.caps
  return { path, caps }
}

/** A straight piece of a route at `at`, from `lo` to `hi`, clear of its bends. */
export type Run = { at: number; lo: number; hi: number }

/** A route's straight pieces, split by direction. */
export type StraightRuns = { horizontal: Run[]; vertical: Run[] }

/** The corners of a route: its points with the in-line ones dropped. */
function corners(points: readonly Point[]): Point[] {
  const out: Point[] = []
  for (const point of points) {
    const last = out[out.length - 1]
    if (last && Math.abs(last.x - point.x) < EPS && Math.abs(last.y - point.y) < EPS) continue
    const before = out[out.length - 2]
    if (
      before &&
      last &&
      ((Math.abs(before.x - last.x) < EPS && Math.abs(last.x - point.x) < EPS) ||
        (Math.abs(before.y - last.y) < EPS && Math.abs(last.y - point.y) < EPS))
    ) {
      out[out.length - 1] = point
      continue
    }
    out.push(point)
  }
  return out
}

/**
 * The straight pieces of a route. A bend eats up to `radius` from each end of
 * a piece that is a corner rather than a pin, and lines that meet inside a
 * bend meet at an angle, so they are not counted as crossing.
 */
export function straightRuns(points: readonly Point[], radius = CABLE_RADIUS): StraightRuns {
  const runs: StraightRuns = { horizontal: [], vertical: [] }
  const corner = corners(points)
  for (let i = 0; i < corner.length - 1; i++) {
    const a = corner[i]
    const b = corner[i + 1]
    if (!a || !b) continue
    const trimStart = i > 0 ? radius : 0
    const trimEnd = i + 1 < corner.length - 1 ? radius : 0
    const flat = Math.abs(a.y - b.y) < EPS
    if (!flat && Math.abs(a.x - b.x) >= EPS) continue
    const [from, to] = flat ? [a.x, b.x] : [a.y, b.y]
    const [lo, hi, trimLo, trimHi] = from < to ? [from, to, trimStart, trimEnd] : [to, from, trimEnd, trimStart]
    if (hi - trimHi <= lo + trimLo) continue
    const run = { at: flat ? a.y : a.x, lo: lo + trimLo, hi: hi - trimHi }
    if (flat) runs.horizontal.push(run)
    else runs.vertical.push(run)
  }
  return runs
}

/** Whether a vertical run and a horizontal one cross, both strictly inside. */
const cross = (v: Run, h: Run) => h.at > v.lo && h.at < v.hi && v.at > h.lo && v.at < h.hi

/** How many times two cables cross each other, whichever gives way. */
export function countCrossings(a: StraightRuns, b: StraightRuns): number {
  let count = 0
  for (const v of a.vertical) for (const h of b.horizontal) if (cross(v, h)) count += 1
  for (const v of b.vertical) for (const h of a.horizontal) if (cross(v, h)) count += 1
  return count
}

/**
 * Where each cable gives way.
 *
 * For every crossing between one cable's vertical run and another's
 * horizontal run, the vertical cable gives way, unless it is in `unbroken`
 * (the selected cables) and the horizontal one is not: then the horizontal
 * cable gives way instead. A jump needs room on the run that gives way; one
 * that would run into a bend is left out, and the two lines simply cross.
 *
 * Horizontal runs are sorted by height so each vertical run only looks at the
 * ones level with it: a 200-cable show stays well under a millisecond.
 */
export function findCrossings(
  routes: ReadonlyArray<{ id: string; points: readonly Point[] }>,
  options: { radius?: number; unbroken?: ReadonlySet<string> } = {},
): Map<string, Jump[]> {
  const { radius = CABLE_RADIUS, unbroken } = options
  const horizontal: Array<Run & { id: string }> = []
  const vertical: Array<Run & { id: string }> = []
  for (const { id, points } of routes) {
    const runs = straightRuns(points, radius)
    for (const run of runs.horizontal) horizontal.push({ ...run, id })
    for (const run of runs.vertical) vertical.push({ ...run, id })
  }

  horizontal.sort((a, b) => a.at - b.at)
  const firstAbove = (y: number) => {
    let low = 0
    let high = horizontal.length
    while (low < high) {
      const mid = (low + high) >> 1
      if ((horizontal[mid]?.at ?? Infinity) <= y) low = mid + 1
      else high = mid
    }
    return low
  }

  const room = JUMP_SIZE + 1
  const fits = (run: Run, at: number) => at - run.lo > room && run.hi - at > room
  const crossings = new Map<string, Jump[]>()
  const add = (id: string, jump: Jump) => {
    const list = crossings.get(id)
    if (!list) crossings.set(id, [jump])
    else if (!list.some((j) => j.x === jump.x && j.y === jump.y && j.on === jump.on)) list.push(jump)
  }

  for (const v of vertical) {
    for (let i = firstAbove(v.lo); i < horizontal.length; i++) {
      const h = horizontal[i]
      if (!h || h.at >= v.hi) break
      if (h.id === v.id || !cross(v, h)) continue
      if (unbroken?.has(v.id) && !unbroken.has(h.id)) {
        if (fits(h, v.at)) add(h.id, { x: v.at, y: h.at, on: 'horizontal' })
      } else if (fits(v, h.at)) {
        add(v.id, { x: v.at, y: h.at })
      }
    }
  }
  for (const list of crossings.values()) list.sort((a, b) => a.x - b.x || a.y - b.y)
  return crossings
}
